import React, { useState } from "react";
import { FiCheckCircle, FiSend } from "react-icons/fi";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import { EstimatedProgressBar } from "./EstimatedProgressBar";
import { MacroProductionStatus } from "./macroDataClient";
import { publierEnProductionEtSignaler } from "./macroProductionPublish";

/**
 * Bandeau + bouton « Publier en production » : promotion des seuls records modifiés,
 * derrière la permission dédiée feature:macro-publish:write (l'upload quotidien n'y
 * donne pas droit). Sans la permission, un état lecture seule est affiché.
 */
export const PublishProductionCard: React.FC<{
	production: MacroProductionStatus;
	canPublish: boolean;
	onPublished: () => void;
}> = ({ production, canPublish, onPublished }) => {
	const [working, setWorking] = useState(false);

	const publish = async () => {
		if (!canPublish || working) return;
		setWorking(true);
		try {
			if (await publierEnProductionEtSignaler()) onPublished();
		} finally {
			setWorking(false);
		}
	};

	const upToDate = production.upToDate;
	const sujets = production.staleRecordIds.length;
	const fiches = production.pendingRateRecordIds?.length ?? 0;
	const aPromouvoir = [
		sujets > 0 ? `${sujets} sujet(s)` : null,
		fiches > 0 ? `${fiches} fiche(s) de taux` : null,
	].filter(Boolean).join(" et ");
	return (
		<div
			className={`rounded-2xl border p-5 shadow-sm ${
				upToDate ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/70"
			}`}
		>
			<div className="flex flex-wrap items-center justify-between gap-4">
			<div className="flex items-start gap-3">
				<FiCheckCircle className={`mt-0.5 h-5 w-5 ${upToDate ? "text-emerald-600" : "text-amber-600"}`} />
				<div>
					<p className={`text-sm font-bold ${upToDate ? "text-emerald-900" : "text-amber-900"}`}>
						{upToDate
							? "Production à jour"
							: `${aPromouvoir || "Des contenus"} à promouvoir en production`}
					</p>
					<p className="mt-0.5 text-xs text-slate-600">
						{production.lastPromotedAtUtc
							? `Dernière promotion : ${new Date(production.lastPromotedAtUtc).toLocaleString("fr-BE")}`
							: "Aucune promotion enregistrée."}
					</p>
				</div>
			</div>
			{canPublish ? (
				<button
					type="button"
					onClick={publish}
					disabled={upToDate || working}
					className="inline-flex items-center gap-2 rounded-lg bg-[#0095DB] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#005CA9] disabled:cursor-not-allowed disabled:opacity-50"
				>
					{working ? <LoadingSpinner size="sm" /> : <FiSend />} Publier en production
				</button>
			) : (
				<p className="text-xs font-medium text-slate-500">
					La promotion en production requiert la permission « macro-publish ».
				</p>
			)}
			</div>
			{/* Durée calibrée sur la mesure réelle du 02/08 (25 s, marge ×2) : la
			    promotion ne touche que les sujets modifiés, elle reste courte. */}
			<EstimatedProgressBar
				active={working}
				estimatedSeconds={60}
				label="Promotion des sujets modifiés vers la production"
			/>
		</div>
	);
};
